/**
 * Accept Invite Page
 *
 * Handles the invitation acceptance flow:
 *  1. Validates the token and fetches invitation details
 *  2. Shows workspace / role / invited-by details
 *  3. Collects name, email, and password from the new user
 *  4. Calls the accept-invite Edge Function (creates Auth user, memberships, roles)
 *  5. Signs the user in and redirects to the app
 *
 * Someone who already has an account does not make a second one. Signed in,
 * they get a Join button (`apply_invitation`). Signed out, the token is saved so
 * that signing in applies it (AuthTelemetryTracker).
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Building2,
  Shield,
  User,
  Eye,
  EyeOff,
  LogIn,
} from 'lucide-react';
import {
  applyInvitation,
  getInvitationDetails,
  storePendingInvite,
  type InvitationWithDetails,
} from '../../lib/invitationService';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { isSuperAdminBySupabaseUserId } from '../../lib/superAdminService';
import type { ActorRole } from '../../types/prioritization/roles';
import { ThemeToggle } from '../../components/ui/ThemeToggle';

// ============================================================================
// Types
// ============================================================================

type PageState =
  | 'loading'
  | 'invalid'
  | 'form'
  | 'join'
  | 'submitting'
  | 'success'
  | 'error';

const ROLE_PRIORITY: ActorRole[] = [
  'system-admin',
  'platform-admin',
  'tenant-admin',
  'workspace-admin',
];

function pickBestRole(roleNames: string[]): ActorRole {
  for (const r of ROLE_PRIORITY) {
    if (roleNames.includes(r)) return r;
  }
  return 'workspace-admin';
}

// ============================================================================
// Edge Function URL helper
// ============================================================================

function getEdgeFunctionUrl(name: string): string {
  const supabase = getSupabaseClient();
  // supabaseUrl is the project URL — edge functions are under /functions/v1/
  const url = (supabase as unknown as { supabaseUrl: string }).supabaseUrl;
  return `${url}/functions/v1/${name}`;
}

// ============================================================================
// Component
// ============================================================================

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setUser = useUserStore((s) => s.setUser);
  const switchRole = useUserStore((s) => s.switchRole);
  const clearImpersonationState = useUserStore((s) => s.clearImpersonationState);
  const signOut = useUserStore((s) => s.signOut);
  const fetchTenants = useTenantStore((s) => s.fetchTenants);
  const setCurrentWorkspace = useTenantStore((s) => s.setCurrentWorkspace);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<InvitationWithDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [signedIn, setSignedIn] = useState<{ id: string; email: string } | null>(null);

  // Form fields — pre-filled from invitation when possible
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ── 1. Validate token on mount ────────────────────────────────────────────

  useEffect(() => {
    async function loadInvitation() {
      if (!token) {
        setErrorMessage('No invitation token provided.');
        setPageState('invalid');
        return;
      }

      const details = await getInvitationDetails(token);

      if (!details) {
        setErrorMessage('Invitation not found. It may have been revoked or the link is incorrect.');
        setPageState('invalid');
        return;
      }
      if (new Date(details.expiresAt) < new Date()) {
        setErrorMessage('This invitation has expired. Ask the sender to create a new one.');
        setPageState('invalid');
        return;
      }
      if (details.usedAt) {
        setErrorMessage('This invitation has already been used. Try logging in, or ask for a new invite.');
        setPageState('invalid');
        return;
      }

      setInvitation(details);
      // Pre-fill form from invitation data
      if (details.name) setName(details.name);
      if (details.email) setEmail(details.email);
      storePendingInvite(token);

      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (session?.user) {
        setSignedIn({ id: session.user.id, email: session.user.email ?? '' });
        setPageState('join');
        return;
      }
      setPageState('form');
    }

    loadInvitation();
  }, [token]);

  // ── 2. Handle form submission ─────────────────────────────────────────────

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Name is required.';
    if (!email.trim()) errors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
    if (password.length < 8) errors.password = 'Password must be at least 8 characters.';
    if (password !== confirmPassword) errors.confirmPassword = 'Passwords do not match.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !token) return;

    setPageState('submitting');
    setErrorMessage('');

    try {
      const supabase = getSupabaseClient();

      // ── Call accept-invite edge function ─────────────────────────────────
      const fnUrl = getEdgeFunctionUrl('accept-invite');
      const { data: { session: anonSession } } = await supabase.auth.getSession();
      const anonKey = (supabase as unknown as { supabaseKey: string }).supabaseKey;

      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${anonSession?.access_token ?? anonKey}`,
        },
        body: JSON.stringify({
          token,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      const result = await res.json() as { userId?: string; email?: string; error?: string };

      if (!res.ok || result.error) {
        setErrorMessage(result.error ?? 'Failed to create account. Please try again.');
        setPageState('error');
        return;
      }

      // ── Sign in with the newly created credentials ───────────────────────
      const { data: authData, error: signInErr } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInErr || !authData.user) {
        setErrorMessage('Account created! Please log in with your new credentials.');
        // Redirect to login so they can sign in — not ideal but safe fallback
        setTimeout(() => navigate('/auth/login', { replace: true }), 3000);
        setPageState('success');
        return;
      }

      const authUser = authData.user;

      // ── Build local session state ────────────────────────────────────────
      const isPlatformAdmin = await isSuperAdminBySupabaseUserId(authUser.id);
      const displayName = name.trim() || authUser.user_metadata?.full_name || email.split('@')[0];
      const displayEmail = authUser.email ?? email.trim();

      clearImpersonationState();
      setUser(authUser.id, displayName, displayEmail);

      if (isPlatformAdmin) {
        switchRole('platform-admin');
      } else {
        const { data: assignments } = await supabase
          .from('role_assignments')
          .select('role_id, roles(name)')
          .eq('subject_type', 'user')
          .eq('subject_id', authUser.id)
          .eq('active', true);

        const roleNames: string[] = [];
        if (assignments) {
          for (const a of assignments) {
            const rn = (a.roles as { name?: string } | null)?.name;
            if (rn) roleNames.push(rn);
          }
        }
        switchRole(pickBestRole(roleNames));
      }

      await fetchTenants(authUser.id);
      setPageState('success');

      setTimeout(() => navigate('/prioritization', { replace: true }), 1500);
    } catch (err) {
      console.error('[AcceptInvite] Error:', err);
      setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred.');
      setPageState('error');
    }
  };

  // ── 3. Join with the account they are signed in to ──

  const handleJoin = async () => {
    if (!token || !signedIn) return;
    setPageState('submitting');
    setErrorMessage('');

    const result = await applyInvitation(token, signedIn.id);
    if (!result.success) {
      setErrorMessage(result.error ?? 'Could not join the workspace. Please try again.');
      setPageState('error');
      return;
    }

    await fetchTenants(signedIn.id);
    if (invitation?.workspaceId) setCurrentWorkspace(invitation.workspaceId);
    setPageState('success');
    setTimeout(() => navigate('/prioritization', { replace: true }), 1500);
  };

  const handleSignOut = () => {
    signOut();
    setSignedIn(null);
    setPageState('form');
  };

  const lockedToAnotherEmail =
    !!signedIn && !!invitation?.email &&
    invitation.email.toLowerCase() !== signedIn.email.toLowerCase();

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface)] dark:bg-[var(--bg)] px-4 transition-colors duration-300">
      <div className="w-full max-w-md">

        {/* ── Loading ─────────────────────────────────────────────────────── */}
        {pageState === 'loading' && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-consulting-slate-blue-100 dark:bg-[var(--active-tint)] rounded-full mb-4">
              <Loader2 className="h-8 w-8 text-consulting-slate-blue-600 dark:text-[var(--text-ter)] animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
              Checking your invitation…
            </h2>
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Just a moment.</p>
          </div>
        )}

        {/* ── Invalid ─────────────────────────────────────────────────────── */}
        {pageState === 'invalid' && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 rounded-full mb-4">
              <XCircle className="h-8 w-8 text-[var(--coral)] dark:text-[var(--coral)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
              Invalid Invitation
            </h2>
            <p className="text-[var(--coral)] dark:text-[var(--coral)] mb-6 text-sm">{errorMessage}</p>
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors text-sm font-medium"
            >
              <LogIn className="h-4 w-4" />
              Go to Login
            </Link>
          </div>
        )}

        {/* ── Signup Form ─────────────────────────────────────────────────── */}
        {pageState === 'form' && invitation && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-8 pt-8 pb-6 border-b border-[var(--rule)] dark:border-[var(--rule)]">
              <div className="flex items-center gap-3 mb-1">
                <div className="p-2 bg-consulting-slate-blue-100 dark:bg-[var(--active-tint)]">
                  <UserPlus className="h-5 w-5 text-consulting-slate-blue-600 dark:text-[var(--text-ter)]" />
                </div>
                <h1 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  You're invited!
                </h1>
              </div>
              <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
                Create your account to join the workspace.
              </p>
            </div>

            {/* Invitation summary */}
            <div className="px-8 py-4 bg-[var(--surface)] dark:bg-[var(--surface)] border-b border-[var(--rule)] dark:border-[var(--rule)] space-y-2.5">
              <div className="flex items-center gap-3">
                <Building2 className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)] flex-shrink-0" />
                <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  Joining <span className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{invitation.workspaceName || 'workspace'}</span>
                </span>
              </div>

              {invitation.roles && invitation.roles.length > 0 && (
                <div className="flex items-start gap-3">
                  <Shield className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)] flex-shrink-0 mt-0.5" />
                  <div className="flex flex-wrap gap-1.5">
                    {invitation.roles.map(role => (
                      <span
                        key={role}
                        className="px-2 py-0.5 bg-consulting-slate-blue-100 dark:bg-[var(--active-tint)] text-consulting-slate-blue-700 dark:text-[var(--text-ter)] text-xs font-medium rounded-full"
                      >
                        {role.replace(/-/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {invitation.invitedByName && (
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)] flex-shrink-0" />
                  <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    Invited by <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">{invitation.invitedByName}</span>
                  </span>
                </div>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Your name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  autoComplete="name"
                  className="no-dark w-full px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 outline-none transition-all"
                />
                {fieldErrors.name && (
                  <p className="mt-1 text-xs text-[var(--coral)] dark:text-[var(--coral)]">{fieldErrors.name}</p>
                )}
              </div>

              {/* Email — locked if the invitation restricts to a specific address */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  readOnly={!!invitation.email}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className={`no-dark w-full px-4 py-3 border  focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 outline-none transition-all
                    ${invitation.email
                      ? 'bg-[var(--surface)] dark:bg-[var(--bg)] text-[var(--text-ter)] dark:text-[var(--text-ter)] border-[var(--rule)] dark:border-[var(--rule)] cursor-not-allowed'
                      : 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] border-[var(--rule)] dark:border-[var(--rule-bright)]'
                    }`}
                />
                {invitation.email && (
                  <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                    This invitation is locked to this email address.
                  </p>
                )}
                {fieldErrors.email && (
                  <p className="mt-1 text-xs text-[var(--coral)] dark:text-[var(--coral)]">{fieldErrors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Create a password
                </label>
                <div className="flex gap-2">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    className="no-dark flex-1 px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="p-2 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] dark:bg-[var(--surface)]"
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-1 text-xs text-[var(--coral)] dark:text-[var(--coral)]">{fieldErrors.password}</p>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Confirm password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  className="no-dark w-full px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 outline-none transition-all"
                />
                {fieldErrors.confirmPassword && (
                  <p className="mt-1 text-xs text-[var(--coral)] dark:text-[var(--coral)]">{fieldErrors.confirmPassword}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 py-3 bg-consulting-slate-blue-600 text-white font-medium hover:bg-consulting-slate-blue-700 transition-colors mt-2"
              >
                <CheckCircle className="h-5 w-5" />
                Create account &amp; join workspace
              </button>

              <p className="text-center text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                Already have an account?{' '}
                <Link to="/auth/login" className="text-consulting-slate-blue-600 dark:text-[var(--text-ter)] hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        )}

        {/* ── Signed in: join with this account ── */}
        {pageState === 'join' && invitation && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-8">
            <h1 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
              Join {invitation.workspaceName || 'this workspace'}
            </h1>
            {lockedToAnotherEmail ? (
              <>
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-6">
                  This invitation is for {invitation.email}. You are signed in as {signedIn?.email}.
                </p>
                <button
                  onClick={handleSignOut}
                  className="w-full py-3 bg-[var(--slate)] hover:opacity-90 text-white text-sm font-medium transition-opacity"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-6">
                  You are signed in as {signedIn?.email}.
                </p>
                <button
                  onClick={handleJoin}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[var(--slate)] hover:opacity-90 text-white text-sm font-medium transition-opacity"
                >
                  <CheckCircle className="h-5 w-5" />
                  Join {invitation.workspaceName || 'this workspace'}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Submitting ──────────────────────────────────────────────────── */}
        {pageState === 'submitting' && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-consulting-slate-blue-100 dark:bg-[var(--active-tint)] rounded-full mb-4">
              <Loader2 className="h-8 w-8 text-consulting-slate-blue-600 dark:text-[var(--text-ter)] animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
              {signedIn ? 'Joining…' : 'Setting up your account…'}
            </h2>
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {signedIn ? 'Adding you to the workspace.' : 'Creating your profile and configuring access.'}
            </p>
          </div>
        )}

        {/* ── Success ─────────────────────────────────────────────────────── */}
        {pageState === 'success' && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 rounded-full mb-4">
              <CheckCircle className="h-8 w-8 text-[var(--green)] dark:text-[var(--text-ter)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
              Welcome to {invitation?.workspaceName || 'the team'}!
            </h2>
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">
              {signedIn ? 'You are in.' : 'Your account is ready.'} Taking you to the app…
            </p>
            <Loader2 className="h-5 w-5 text-[var(--text-ter)] animate-spin mx-auto" />
          </div>
        )}

        {/* ── Error ───────────────────────────────────────────────────────── */}
        {pageState === 'error' && (
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 rounded-full mb-4">
              <XCircle className="h-8 w-8 text-[var(--coral)] dark:text-[var(--coral)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
              Something went wrong
            </h2>
            <p className="text-[var(--coral)] dark:text-[var(--coral)] mb-6 text-sm">{errorMessage}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setPageState(signedIn ? 'join' : 'form')}
                className="flex-1 px-4 py-2.5 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors text-sm font-medium"
              >
                Try Again
              </button>
              <Link
                to="/auth/login"
                className="flex-1 px-4 py-2.5 bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors text-sm font-medium text-center"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}

        {/* Theme toggle */}
        <div className="mt-4 flex justify-center">
          <ThemeToggle variant="labeled" />
        </div>
      </div>
    </div>
  );
}

// local alias so we don't need an extra import
function UserPlus(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24" height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  );
}
